import { ReadonlyURLSearchParams } from 'next/navigation';

type SearchParamsLike = ReadonlyURLSearchParams | URLSearchParams | Record<string, string | undefined>;

const UTM_TRACKING_KEYS = ['utm_source', 'utm_medium', 'utm_campaign'] as const;
const TRACKING_QUERY_KEYS = [...UTM_TRACKING_KEYS, 'utm_term', 'utm_content', 'fbclid', 'gclid', 'msclkid', 'ref'] as const;

const LANDING_UTM_STORAGE_KEY = 'landingUtmParams';
export type UtmTrackingKey = typeof UTM_TRACKING_KEYS[number];
export type UtmTrackingParams = Partial<Record<UtmTrackingKey, string>>;

// URL param keys
export const URL_PARAMS = {
  SHOW_ID: 'show-id',
  EPISODE_NUMBER: 'episode-number',
  PLAN: 'plan',
  AUTO: 'auto',
  PHONE: 'phone',
  LINK_TOKEN: 'link-token',
  PAYMENT_APP: 'payment-app',
  RETURN_PATH: 'return-path',
  COUPON_CODE: 'COUPON_CODE',
} as const;

/** Sanitise a user-supplied return path so it can only navigate within the same origin. */
export const sanitizeReturnPath = (raw: string | null | undefined): string => {
  if (!raw || !raw.startsWith('/') || raw.startsWith('//')) return '/';
  return raw;
};

// Build URL params from search params
const readParam = (searchParams: SearchParamsLike, key: string): string | null => {
  if (typeof (searchParams as ReadonlyURLSearchParams).get === 'function') {
    return (searchParams as ReadonlyURLSearchParams).get(key);
  }

  return (searchParams as Record<string, string | undefined>)[key] ?? null;
};

const pickParams = <TKey extends string>(searchParams: SearchParamsLike, keys: readonly TKey[]) =>
  keys.reduce<Partial<Record<TKey, string>>>((acc, key) => {
    const value = readParam(searchParams, key);
    if (value) acc[key] = value;
    return acc;
  }, {});

export const buildUrlParams = (
  searchParams: SearchParamsLike,
  keys: string[],
  additionalParams?: Record<string, string>
): string => {
  const params = new URLSearchParams();
  const allKeys = [...new Set([...keys, ...TRACKING_QUERY_KEYS])];

  allKeys.forEach(key => {
    const value = readParam(searchParams, key);
    if (value) params.set(key, value);
  });

  if (additionalParams) {
    Object.entries(additionalParams).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
  }

  return params.toString();
};

const getUtmTrackingParams = (searchParams: SearchParamsLike): UtmTrackingParams =>
  pickParams(searchParams, UTM_TRACKING_KEYS);

const getStoredUtmTrackingParams = (): UtmTrackingParams => {
  if (typeof window === 'undefined') return {};

  const storedValue = sessionStorage.getItem(LANDING_UTM_STORAGE_KEY);
  if (!storedValue) return {};

  try {
    const parsedValue = JSON.parse(storedValue);
    if (!parsedValue || typeof parsedValue !== 'object') return {};

    return pickParams(parsedValue as Record<string, string | undefined>, UTM_TRACKING_KEYS);
  } catch {
    return {};
  }
};

export const persistUtmTrackingParams = (searchParams: SearchParamsLike) => {
  if (typeof window === 'undefined') return;

  const params = getUtmTrackingParams(searchParams);
  const hasUtmValues = Object.values(params).some(Boolean);
  if (!hasUtmValues) return;

  sessionStorage.setItem(LANDING_UTM_STORAGE_KEY, JSON.stringify(params));
};

export const resolveUtmTrackingParams = (searchParams?: SearchParamsLike): UtmTrackingParams => {
  if (!searchParams) return getStoredUtmTrackingParams();

  return {
    ...getStoredUtmTrackingParams(),
    ...getUtmTrackingParams(searchParams),
  };
};

// Build route with optional params
export const buildRoute = (path: string, searchParams: SearchParamsLike, keys: string[]): string => {
  const params = buildUrlParams(searchParams, keys);
  return params ? `${path}?${params}` : path;
};

// Get content params (show-id, episode-num, link-token)
export const getContentParams = (searchParams: SearchParamsLike) => {
  const params = pickParams(searchParams, [
    URL_PARAMS.SHOW_ID,
    URL_PARAMS.EPISODE_NUMBER,
    URL_PARAMS.LINK_TOKEN,
  ]);

  return {
    showId: params[URL_PARAMS.SHOW_ID] ?? null,
    episodeNum: params[URL_PARAMS.EPISODE_NUMBER] ?? null,
    linkToken: params[URL_PARAMS.LINK_TOKEN] ?? null,
  };
};

// Build Play Store URL
export const buildPlayStoreUrl = (showId: string | null, episodeNum: string | null, linkToken: string | null): string => {
  if (showId && episodeNum && linkToken) {
    const url = `https://alrighttv.go.link/m/${showId}/${episodeNum}?adj_t=${linkToken}`;
    return url;
  }
  return `https://alrighttv.go.link/m/693d6b26a3fe4d0d704c8435/1?adj_t=1xx1zeqm`
  //'https://play.google.com/store/apps/details?id=com.app.alright&hl=en_IN';
};
